import { useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronRight, Mails } from "lucide-react";
import moment from "moment-timezone";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";
import { DEFAULT_PAGE_SIZE } from "constants/Constants";
import LogEntryCard from "components/LogItem/LogEntryCard";
import { resolveEventEntry } from "components/LogItem/eventEntry";
import AuditService from "services/Audit";

// Worded to match each action's own entry ("Email Sent", "Email Opened", ...).
const DIGEST_COPY = {
  delivered: { title: "Emails Sent", verb: "sent" },
  opened: { title: "Emails Opened", verb: "opened" },
  clicked: { title: "Email Links Clicked", verb: "clicked" },
};

/**
 * Emails filter params that list one digest's emails individually.
 *
 * Any Emails filter turns the digest off server-side, so the same endpoint
 * returns the rows the digest stands for. The feed's other filters are kept so
 * the expansion matches what the digest counted.
 */
export function digestExpansionFilters({ action, day }, feedFilters = {}) {
  return {
    ...feedFilters,
    emails: JSON.stringify({ op: "in", value: [`email_${action}`] }),
    created_at: JSON.stringify({ op: "between", value: [day, day] }),
  };
}

const DigestEmails = ({
  digest,
  modelId,
  modelType,
  feedFilters,
  dispositions,
  refreshLog,
}) => {
  const filters = digestExpansionFilters(digest, feedFilters);
  const { data, isPending, isError, refetch, hasNextPage, fetchNextPage } =
    useInfiniteQuery({
      queryKey: ["audit-log-digest", modelType, modelId, filters],
      queryFn: ({ pageParam }) =>
        AuditService.getAuditLogs({
          modelId,
          modelType,
          page: pageParam,
          filters,
        }),
      initialPageParam: 1,
      getNextPageParam: (lastPage, _allPages, lastPageParam) =>
        lastPageParam * DEFAULT_PAGE_SIZE < (lastPage.total ?? 0)
          ? lastPageParam + 1
          : undefined,
    });

  if (isPending) {
    return (
      <div className="tw:flex tw:justify-center tw:py-3">
        <Spinner />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="tw:flex tw:items-center tw:gap-2 tw:py-3 tw:text-sm tw:text-(--color-text-muted)">
        <span>Could not load these emails.</span>
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          Retry
        </Button>
      </div>
    );
  }

  const emails = data.pages.flatMap(page => page.data ?? []);
  return (
    <div className="tw:mt-3 tw:flex tw:flex-col tw:gap-2">
      {emails.map(email => (
        <LogEntryCard
          key={email.id}
          item={email}
          entry={resolveEventEntry(email)}
          modelType={modelType}
          modelId={modelId}
          dispositions={dispositions}
          refreshLog={refreshLog}
        />
      ))}
      {hasNextPage && (
        <Button
          variant="outline"
          size="sm"
          className="tw:self-center"
          onClick={() => fetchNextPage()}
        >
          Show more
        </Button>
      )}
    </div>
  );
};

/**
 * One row standing for a day's routine emails of one action on a campaign.
 *
 * Expanding it lists those emails, fetched on demand: a busy campaign can send
 * hundreds a day.
 */
const EmailDigestEntry = ({
  ref,
  item,
  modelId,
  modelType,
  activeItem,
  feedFilters,
  dispositions,
  refreshLog,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { digest } = item;
  const copy = DIGEST_COPY[digest.action];
  // The backend groups on the UTC day, while each email's own time is shown
  // in the viewer's zone, so the day says which calendar it belongs to.
  const dayLabel = `${moment(digest.day, "YYYY-MM-DD").format("MMM D, YYYY")} (UTC)`;
  const ToggleIcon = isExpanded ? ChevronDown : ChevronRight;

  return (
    <Card
      size="sm"
      ref={ref}
      className={cn("tw:w-full", activeItem && "highlight")}
    >
      <CardContent>
        <div className="tw:flex tw:gap-3">
          <Mails
            className="tw:mt-0.5 tw:size-6 tw:shrink-0 tw:text-(--color-text-secondary)"
            aria-hidden="true"
          />
          <div className="tw:min-w-0 tw:flex-1">
            <div className="tw:flex tw:items-start tw:gap-2">
              <div className="tw:min-w-0 tw:flex-1">
                <h4 className="tw:m-0! tw:leading-5 tw:[font-family:var(--font-heading)] tw:text-sm tw:font-semibold tw:text-(--color-text-primary)">
                  {copy.title}
                </h4>
                <div className="tw:text-sm tw:text-(--color-text-secondary)">
                  {dayLabel}
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                aria-expanded={isExpanded}
                onClick={() => setIsExpanded(expanded => !expanded)}
              >
                <ToggleIcon data-icon="inline-start" />
                {isExpanded ? "Hide" : "Show"} emails
              </Button>
            </div>
            <p className="tw:mt-3 tw:mb-0! tw:text-(--color-text-primary)">
              {digest.count} emails were {copy.verb} on {dayLabel}.
            </p>
            {isExpanded && (
              <DigestEmails
                digest={digest}
                modelId={modelId}
                modelType={modelType}
                feedFilters={feedFilters}
                dispositions={dispositions}
                refreshLog={refreshLog}
              />
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default EmailDigestEntry;
